import React from 'react';
import {
	Drawer as MuiDrawer,
	Box,
	useMediaQuery,
	useTheme,
	IconButton,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FullScreenDialog from './FullScreenDialog';

interface DrawerProps {
	open: boolean;
	handleClose: () => void;
	children: React.ReactNode;
	title?: string;
	anchor?: 'left' | 'right' | 'top' | 'bottom';
	width?: number | string;
	sx?: any;
	disableBackdropClose?: boolean;
	disableFullScreen?: boolean;
}

const Drawer: React.FC<DrawerProps> = ({
	open,
	handleClose,
	children,
	title,
	anchor = 'right',
	width = 600,
	sx = {},
	disableBackdropClose = false,
	disableFullScreen = false,
}) => {
	const theme = useTheme();
	const mobileBreakpoint = useMediaQuery(theme.breakpoints.down('md'));

	// Use FullScreenDialog on mobile if not disabled
	if (!disableFullScreen && mobileBreakpoint) {
		return (
			<FullScreenDialog
				open={open}
				handleClose={handleClose}
				title={title}
				children={children}
			/>
		);
	}

	return (
		<MuiDrawer
			anchor={anchor}
			open={open}
			onClose={disableBackdropClose ? undefined : handleClose}
			sx={{
				'& .MuiDrawer-paper': {
					width: typeof width === 'number' ? `${width}px` : width,
					maxWidth: '100vw',
					height: '100vh',
					display: 'flex',
					flexDirection: 'column',
				},
				...sx,
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
					bgcolor: 'background.paper',
				}}
			>
				{/* Header */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						p: 3,
						pb: 0,
						minHeight: 64,
						position: 'relative',
					}}
				>
					{title && <Typography variant="h6">{title}</Typography>}
					<IconButton
						onClick={handleClose}
						sx={{
							position: 'absolute',
							right: 5,
							top: 5,
							color: 'text.label',
						}}
					>
						<CloseIcon />
					</IconButton>
				</Box>

				{/* Content */}
				<Box
					sx={{
						flex: 1,
						overflow: 'auto',
						p: 3,
					}}
				>
					{children}
				</Box>
			</Box>
		</MuiDrawer>
	);
};

export default Drawer;
